import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Linkedin, Instagram, X, Sparkles, FileText, Home, Box, User } from 'lucide-react';

export default function Footer() {
  const location = useLocation();

  const fbLink = process.env.REACT_APP_ASUS_FB_LINK || '#';
  const instagramLink = process.env.REACT_APP_ASUS_INSTAGRAM_LINK || '#';
  const linkedinLink = process.env.REACT_APP_ASUS_LINKEDIN_LINK || '#';
  const xLink = process.env.REACT_APP_ASUS_X_LINK || '#';

  const PhoneLink = process.env.REACT_APP_PHONE_LINK || '#';
  const PhoneLink2 = process.env.REACT_APP_PHONE_LINK2 || '#';
  const EmailLink = process.env.REACT_APP_EMAIL_LINK || '#';
  const EmailLink2 = process.env.REACT_APP_EMAIL_LINK2 || '#';
  const devLink = process.env.REACT_APP_DEV_LINKEDIN_LINK || '#';

  const socialLinks = [
    { icon: <Facebook size={20} />, link: fbLink },
    { icon: <X size={20} />, link: xLink },
    { icon: <Linkedin size={20} />, link: linkedinLink },
    { icon: <Instagram size={20} />, link: instagramLink },
  ];

  const handleNavClick = (path: string) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const mobileLinks = [
    { name: 'Home', icon: <Home size={20} />, path: '/' },
    { name: 'Products', icon: <Box size={20} />, path: '/products' },
    { name: 'Catalogues', icon: <FileText size={20} />, path: '/catalogues' },
    { name: 'About', icon: <User size={20} />, path: '/about-us' },
    { name: 'Contact', icon: <Phone size={20} />, path: '/contact-us' },
  ];

  return (
    <footer className="bg-slate-900 text-white w-full">
      {/* Contact CTA */}
      <div className="bg-orange-600 text-white py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 text-center">
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3">
            Need Help Finding the Right Part?
          </h3>
          <p className="text-orange-100 mb-4 sm:mb-6 text-sm sm:text-base">
            Our technical experts are ready to assist you with product selection and compatibility.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href={`tel:${PhoneLink2}`}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-sm sm:text-base"
            >
              <Phone size={18} className="mr-2" /> Call Now
            </a>

            <Link
              to="/contact-us"
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-orange-500 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors text-sm sm:text-base"
              onClick={() => handleNavClick('/contact-us')}
            >
              <FileText size={20} className="mr-2" />
              Request a Quote
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-6 px-3 sm:px-4 lg:px-6 w-full">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center md:text-left">

          {/* Company Info */}
          <div className="flex flex-col items-center md:items-start space-y-4 md:col-span-2 w-full">
            <Link
              to="/"
              onClick={() => handleNavClick('/')}
              className="flex flex-col sm:flex-row items-center sm:items-start space-y-2 sm:space-y-0 sm:space-x-4 w-full"
            >
              <div className="flex-shrink-0">
                <img
                  decoding="async"
                  src="../Asus_logo.png"
                  alt="ASUS Logo"
                  className="h-20 w-auto object-contain"
                />
              </div>
              <div className="flex flex-col items-center sm:items-start">
                <div className="font-extrabold text-white text-xl sm:text-2xl md:text-3xl mb-1">
                  Parnyab Enterprises Pvt. Ltd.
                </div>
                <div className="text-orange-500 text-xs sm:text-sm font-semibold uppercase tracking-wider">
                  Precisely Engineered,
                </div>
                <div className="text-orange-500 text-xs sm:text-sm font-semibold uppercase tracking-wider">
                  Optimum Performance.
                </div>
              </div>
            </Link>

            <div className="flex flex-col space-y-2 w-full md:max-w-full lg:max-w-md text-center md:text-left">
              <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                Powering your machines with{" "}
                <span className="text-orange-500 font-semibold">premium spare parts</span>{" "}
                for unmatched <span className="font-semibold">performance</span> and{" "}
                <span className="font-semibold">reliability</span>. We specialize in{" "}
                <span className="text-orange-500 font-semibold">backhoe loader components</span>,
                ensuring your equipment runs efficiently and lasts longer.
              </p>
            </div>

            <div className="flex justify-center sm:justify-start mt-2 space-x-3 flex-wrap">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-slate-800 hover:bg-orange-600 p-2 sm:p-3 rounded-lg transition-colors"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-extrabold text-lg mb-2">Quick Links</h3>
            <ul className="flex flex-row flex-wrap justify-center md:flex-col md:justify-start gap-4 md:gap-2">
              {[
                { name: 'Home', path: '/' },
                { name: 'Products', path: '/products' },
                { name: 'Catalogues', path: '/catalogues' },
                { name: 'About Us', path: '/about-us' },
                { name: 'Contact Us', path: '/contact-us' }
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={() => handleNavClick(link.path)}
                    className="text-gray-300 font-semibold hover:text-orange-500 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="font-bold text-lg mb-4">Contact Info</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 justify-center md:justify-start flex-wrap">
                <Phone className="text-orange-500 mt-1" size={18} />
                <div>
                  <p className="text-gray-300">{PhoneLink}</p>
                  <p className="text-gray-300">{PhoneLink2}</p>
                  <p className="text-sm text-gray-400">Mon-Sat: 9AM-7PM IST</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 justify-center md:justify-start flex-wrap">
                <Mail className="text-orange-500 mt-1" size={18} />
                <div>
                  <p className="text-gray-300">{EmailLink}</p>
                  <p className="text-gray-300">{EmailLink2}</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 justify-center md:justify-start flex-wrap">
                <MapPin className="text-orange-500 mt-1" size={18} />
                <div>
                  <p className="text-gray-300">Parnyab Enterprises Pvt. Ltd.</p>
                  <p className="text-gray-300">West Rajiv Nagar, Sector-12</p>
                  <p className="text-gray-300">Gurugram, Haryana-122001, INDIA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Section */}
      <div className="bg-slate-900 border-t border-slate-800 w-full">
        <div className="py-2 px-3 sm:px-4 w-full text-center">
          <p className="text-gray-400 text-xs leading-relaxed">
            Disclaimer: All OEM names, logos, part numbers, and images are used solely for identification and reference purposes.
            "Asus" is a brand of Parnyab Enterprises Pvt. Ltd., an independent entity.
            We are not affiliated with, endorsed by, or authorized by any OEM.
            Products sold under the "Asus" brand are aftermarket parts and do not imply any OEM warranty or representation.
            Customers are responsible for verifying compatibility before purchase.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-slate-900 border-t border-slate-800 w-full">
        <div className="py-2 px-3 sm:px-4 w-full flex flex-col sm:flex-row justify-center sm:justify-between items-center gap-2 text-center">
          <div className="text-gray-400 text-xs font-semibold">
            © 2025 Parnyab Enterprises Pvt Ltd. All rights reserved.
          </div>
          <a
            href={devLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-1 text-xs text-gray-400 font-semibold hover:text-orange-500 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            <span>Designed & Developed by Sahil Aggarwal</span>
          </a>
        </div>
      </div>

      {/* Mobile Bottom Menu Spacer */}
      <div className="h-16 max-[1250px]:block hidden"></div>

      {/* Mobile / Tablet Bottom Menu (visible up to 1250px width) */}
      <div className="
  fixed bottom-0 left-0 right-0 
  bg-gradient-to-t from-slate-900/95 to-slate-800/90 
  backdrop-blur-md border-t border-slate-700 
  justify-between items-center px-4 py-1 
  z-50 shadow-[0_-2px_10px_rgba(0,0,0,0.4)]
  hidden max-[1250px]:flex
">
        {mobileLinks.map((link) => {
          const isActive = location.pathname === link.path;
          return (
            <Link
              key={link.name}
              to={link.path}
              onClick={() => handleNavClick(link.path)}
              className={`
          flex flex-col items-center justify-center flex-1 transition-all duration-300 ease-out
          ${isActive
                  ? 'text-orange-400 scale-105 drop-shadow-[0_0_8px_rgba(255,165,0,0.5)]'
                  : 'text-gray-400 hover:text-orange-400 hover:scale-105'}
        `}
            >
              <div
                className={`
            p-1.5 rounded-full transition-all duration-300 ease-out
            ${isActive
                    ? 'bg-gradient-to-br from-orange-500/30 to-orange-400/20 shadow-[0_2px_8px_rgba(255,165,0,0.4)]'
                    : 'bg-transparent hover:bg-orange-400/10'}
          `}
              >
                {link.icon}
              </div>
              <span
                className={`text-[10px] mt-0.5 font-medium tracking-wide ${isActive ? 'text-orange-400' : ''}`}
              >
                {link.name}
              </span>
            </Link>
          );
        })}
      </div>
    </footer>
  );
};
